import { type FC } from 'react';
import { observer } from 'mobx-react-lite';
import { DefaultOptionType } from 'antd/es/select';
import { Button, CheckboxGroup, Space, Text } from '@g2u/ui-kit';

import { JsonDataItem, type WithOptionalDataTest } from 'types';
import { NotificationsFields, Section } from 'components';
import { useText } from 'helpers/hooks/useText';
import { BookingStore } from 'store';

type Props = {
  title?: string;
  isLoading?: boolean;
  isNotified?: boolean;
  hasSkipButton?: boolean;
  hasScript?: boolean;
  isShort?: boolean;
  isStandalone?: boolean;
  onSubmit?: (data: JsonDataItem | null) => void;
  emailOptions?: DefaultOptionType[];
  smsOptions?: DefaultOptionType[];
  initialValues?: NotificationsFields.InitialValues;
} & WithOptionalDataTest;

export enum Type {
  NewService = 'new_service',
  Reschedule = 'reschedule',
}

const SendNotificationComponent: FC<Props> = observer((props) => {
  const {
    onSubmit,
    isLoading,
    isNotified,
    title,
    hasScript,
    hasSkipButton,
    initialValues,
    emailOptions,
    smsOptions,
    isStandalone,
    isShort,
  } = props;
  const { appointment } = BookingStore;
  const { getText } = useText();

  return (
    <Section title={title ?? 'Send Notifications'}>
      <Space.$ direction={Space.Direction.Vertical} size={Space.Size.Middle}>
        {hasScript && appointment && (
          <Space.$ direction={Space.Direction.Vertical} size={Space.Size.ExtraSmall} className="script-box">
            <Text.$ mark>{getText('booking.confirmation.technicianAppointment')}</Text.$>
            <Text.$>{getText('booking.confirmation.appointmentConfirmation')}</Text.$>
          </Space.$>
        )}

        {isShort ? (
          <CheckboxGroup.$
            dataTestId="notifications"
            initialValue={['C', 'T']}
            name="notifications"
            label={<>Send SMS and email to: &nbsp;</>}
            columns={0}
            options={[
              {
                label: <>Customer</>,
                value: 'C',
              },
              {
                label: <>Technician</>,
                value: 'T',
              },
            ]}
          />
        ) : (
          <NotificationsFields
            isStandalone={isStandalone}
            actionTitle="Send Notification"
            onSubmit={onSubmit}
            isLoading={isLoading}
            isNotified={isNotified}
            hasSkipButton={hasSkipButton}
            initialValues={initialValues}
            emailOptions={emailOptions}
            smsOptions={smsOptions}
            actionButtonTheme={Button.Type.Primary}
          />
        )}
      </Space.$>
    </Section>
  );
});

export const SendNotification = SendNotificationComponent as typeof SendNotificationComponent & { Type: typeof Type };

SendNotification.Type = Type;
type TypeDef = Type;

export namespace SendNotification {
  export type Type = TypeDef;
}
